<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("pricing.define_pricing") }}</template>
        <template v-slot:body>
            <div class="columns" v-if="initDone && isPaymentGatewayConfigured">
                <div class="column">
                    <div class="field">
                        <label class="label">{{ translate("pricing.registration_stage") }}:</label>
                        <div class="control">
                            <div class="select">
                                <select v-model="selectedStageId">
                                    <option :value="stage.id" v-for="stage in registrationStages" :key="stage.id">
                                        <span v-if="stage.id > 0">
                                            #{{ stage.numberOfStage }}. {{ convertToTimezone(stage.startDate, eventTimezone) }} -
                                            {{ convertToTimezone(stage.endDate, eventTimezone) }}
                                        </span>
                                        <span v-else>------</span>
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <label class="label">{{ translate("pricing.fixed_price_for_single_category") }}:</label>

                    <div
                        class="field is-grouped"
                        :class="getFieldClassForPrice(singleCategoryPricesMap[currency])"
                        v-for="currency in supportedCurrencies"
                        :key="currency"
                    >
                        <div class="control">
                            <input class="input" type="text" v-model="singleCategoryPricesMap[currency]" />
                            <p v-if="getFieldClassForPrice(singleCategoryPricesMap[currency]) !== ''" class="help">
                                {{ translate("pricing.provide_valid_price") }}
                            </p>
                        </div>
                        <div class="control is-flex is-align-items-center">
                            <p class="has-text-weight-bold">{{ currency }}</p>
                        </div>
                    </div>

                    <div class="mb-3" v-for="(categoryDefinition, index) in categoriesDefinitions" :key="index">
                        <hr />
                        <div>
                            <label class="label mb-2">
                                <span>#{{ index + 1 }} {{ translate("common.category") }}:</span>
                                <a class="is-pulled-right has-text-danger" @click="removeDefinitionForCategory(index)">
                                    <span class="icon">
                                        <i class="fa fa-times" />
                                    </span>
                                    <span>{{ translate("common.delete") }}</span></a
                                >
                            </label>
                        </div>
                        <div class="field">
                            <div class="control">
                                <div class="select is-fullwidth">
                                    <select v-model="categoryDefinition.parameterId">
                                        <option value="0">-----</option>
                                        <option v-for="category in allLevelsCategories" :value="category.key" :key="category.key">
                                            {{ category.value }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div
                            class="field is-grouped"
                            :class="getFieldClassForPrice(categoryDefinition.prices[currency])"
                            v-for="currency in supportedCurrencies"
                            :key="currency"
                        >
                            <div class="control">
                                <input class="input" type="text" v-model="categoryDefinition.prices[currency]" />
                                <p v-if="getFieldClassForPrice(categoryDefinition.prices[currency]) !== ''" class="help">
                                    {{ translate("pricing.provide_valid_price") }}
                                </p>
                            </div>
                            <div class="control is-flex is-align-items-center">
                                <p class="has-text-weight-bold">{{ currency }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="field mt-1">
                        <div class="control">
                            <button class="button is-info" @click="addDefinitionForCategory">
                                <span class="icon">
                                    <i class="fas fa-plus-circle"></i>
                                </span>
                                <span>{{ translate("pricing.define_for_category") }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="initDone && !isPaymentGatewayConfigured" class="columns">
                <div class="column">
                    <Notification class="is-danger">
                        <span class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </span>
                        <span>{{ translate("pricing.payment_gateway_not_configured") }}</span>
                    </Notification>
                </div>
            </div>
            <div class="columns is-centered is-mobile" v-else>
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="savingPricingInProgress" class="button is-primary" @click="confirm" :disabled="!isFormValid">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Notification from "../Common/Notification.vue";
    import { translate } from "../Common/translations";
    import { isPositiveIntOrEmpty } from "../Common/numbers";
    import { handleError } from "../Common/errorHandler";
    import pricingApi from "../Api/pricing";
    import registrationStagesApi, { RegistrationStage } from "../Api/registrationStages";
    import { CategoryDefinedPriceRawAttribute, FixedPriceForSingleCategoryRawAttribute } from "../Common/Pricing/types";
    import { usePricingSetupStore } from "./pricingSetupApp";
    import { convertToTimezone } from "../Common/dateTimeUtils";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();
    const store = usePricingSetupStore();

    const initDone = ref(false);
    const savingPricingInProgress = ref(false);
    const editMode = ref(false);
    const pricingIdForUpdate = ref(0);
    const registrationStages = ref<RegistrationStage[]>([]);
    const selectedStageId = ref(0);
    const singleCategoryPricesMap = ref<Record<string, number>>({});
    const supportedCurrencies = ref<string[]>([]);

    type CategoryDefinition = {
        parameterId: number;
        prices: Record<string, number | null>;
    };

    const categoriesDefinitions = ref<CategoryDefinition[]>([]);
    const modal = ref<InstanceType<typeof RegularModal> | null>(null);

    const allLevelsCategories = computed(() => store.allLevelsCategories);
    const isPaymentGatewayConfigured = computed(() => supportedCurrencies.value.length > 0);
    const isFormValid = computed(() => {
        if (selectedStageId.value === 0) {
            return false;
        }
        const filteredPrices = Object.keys(singleCategoryPricesMap.value).filter(key => singleCategoryPricesMap.value[key] > 0);
        if (filteredPrices.length !== supportedCurrencies.value.length) {
            return false;
        }

        const validDefinitions = categoriesDefinitions.value.filter(categoryDefinition => {
            if (categoryDefinition.parameterId === 0) {
                return false;
            }

            const validPrices = Object.keys(categoryDefinition.prices).filter(currency => {
                const priceForCurrency = categoryDefinition.prices[currency];
                if (priceForCurrency === null) {
                    return false;
                }
                return priceForCurrency > 0;
            });
            return validPrices.length === supportedCurrencies.value.length;
        });
        return validDefinitions.length === categoriesDefinitions.value.length;
    });

    const open = () => {
        initDone.value = false;
        savingPricingInProgress.value = false;
        singleCategoryPricesMap.value = {};
        supportedCurrencies.value = [];
        categoriesDefinitions.value = [];
        modal.value?.open();

        return Promise.all([fetchSupportedCurrencies(), fetchRegistrationStages()])
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    };

    const getFieldClassForPrice = value => {
        if (isPositiveIntOrEmpty(value)) {
            return "";
        } else {
            return "has-error";
        }
    };

    const openForEdit = pricingDefinition => {
        return open().then(() => {
            editMode.value = true;
            selectedStageId.value = pricingDefinition.registrationStageId;
            pricingIdForUpdate.value = pricingDefinition.id;

            registrationStages.value = registrationStages.value.filter(stage => stage.id === selectedStageId.value);

            pricingDefinition.attributes.forEach(attr => {
                if (attr.name === "fixed_price_for_single_category") {
                    attr.value.forEach(([price, currency]) => {
                        singleCategoryPricesMap.value[currency] = price;
                    });
                }
                if (attr.name === "category_defined_price") {
                    const categoryDefinition: CategoryDefinition = {
                        parameterId: attr.value.parameterId,
                        prices: {},
                    };

                    const exists = allLevelsCategories.value.find(category => category.key === attr.value.parameterId);
                    if (!exists) {
                        return;
                    }

                    const pricesForCategory = {};
                    attr.value.prices.forEach(({ value, currency }) => {
                        pricesForCategory[currency] = value;
                    });
                    categoryDefinition.prices = pricesForCategory;
                    categoriesDefinitions.value.push(categoryDefinition);
                }
            });
        });
    };

    const fetchRegistrationStages = () => {
        return registrationStagesApi
            .getAll(props.eventId)
            .then(responseArray => {
                registrationStages.value = [
                    { id: 0, eventId: 0, numberOfStage: 0, endDate: "", startDate: "", totalDays: 0 },
                    ...responseArray,
                ];
                selectedStageId.value = 0;
            })
            .catch(handleError);
    };

    const fetchSupportedCurrencies = () => {
        return pricingApi.getConfiguredPaymentGateway(props.eventId).then(configuredPaymentGateway => {
            supportedCurrencies.value = configuredPaymentGateway.currencies || [];
        });
    };

    const close = () => {
        modal.value?.close();
    };

    defineExpose({ open, openForEdit });

    const confirm = () => {
        savingPricingInProgress.value = true;

        const prices: FixedPriceForSingleCategoryRawAttribute["value"] = [];
        for (const [currency, price] of Object.entries(singleCategoryPricesMap.value)) {
            prices.push([~~price, currency]);
        }

        const fixedPriceForSingleCategoryAttribute = {
            name: "fixed_price_for_single_category",
            value: prices,
        } as FixedPriceForSingleCategoryRawAttribute;

        const definedPricesForCategoriesAttributes = categoriesDefinitions.value.map(category => {
            const pricesForCategory: Array<{ currency: string; value: number | null }> = [];
            for (const [currency, priceForCategory] of Object.entries(category.prices)) {
                pricesForCategory.push({
                    currency: currency,
                    value: priceForCategory,
                });
            }

            return {
                name: "category_defined_price",
                value: {
                    parameterId: category.parameterId,
                    prices: pricesForCategory,
                },
            } as CategoryDefinedPriceRawAttribute;
        });

        return Promise.resolve()
            .then(() => {
                if (editMode.value) {
                    return pricingApi.updatePricing({
                        id: pricingIdForUpdate.value,
                        eventId: props.eventId,
                        registrationStageId: selectedStageId.value,
                        rawAttributes: [fixedPriceForSingleCategoryAttribute, ...definedPricesForCategoriesAttributes],
                    });
                } else {
                    return pricingApi.addNewPricing(props.eventId, selectedStageId.value, [
                        fixedPriceForSingleCategoryAttribute,
                        ...definedPricesForCategoriesAttributes,
                    ]);
                }
            })
            .then(() => {
                return store.refreshPricingDefinitions();
            })
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => {
                savingPricingInProgress.value = false;
            });
    };

    const addDefinitionForCategory = () => {
        const currencyToPriceForOverride: Record<string, number | null> = {};
        supportedCurrencies.value.forEach(currency => {
            currencyToPriceForOverride[currency] = null;
        });

        categoriesDefinitions.value.push({
            parameterId: 0,
            prices: currencyToPriceForOverride,
        });
    };

    const removeDefinitionForCategory = index => {
        categoriesDefinitions.value.splice(index, 1);
    };
</script>

<style scoped></style>
